// @ts-nocheck
/**
 * Product Builder — shared audio waveform drawing (live scroll + static playback).
 * Used by pb-longtext-recorder.js and pb-audio-recorder.js.
 *
 * Canvas draws are deferred until the element has a measurable layout width so
 * hidden builder steps (cart edit hydrate, topic sub-steps) do not bake blurry
 * low-res buffers that get CSS-stretched when the step becomes visible.
 */
(function () {
  'use strict';

  var BAR_COUNT = 96;
  var BAR_WIDTH = 4;
  var BAR_GAP = 5;
  var LIVE_SAMPLE_MS = 42;
  var SILENCE_THRESHOLD = 0.035;

  var canvasStates = new WeakMap();
  var canvasObservers = new WeakMap();
  var scheduledCanvases = new Set();

  function getLogicalSize(canvas) {
    var hAttr = parseInt(canvas.getAttribute('height'), 10);
    return {
      w: canvas.clientWidth || canvas.offsetWidth || 0,
      h: canvas.clientHeight || hAttr || 32
    };
  }

  function isMeasurable(canvas) {
    if (!canvas || !canvas.isConnected) return false;
    return getLogicalSize(canvas).w > 0;
  }

  function getCtx(canvas) {
    if (!canvas || !isMeasurable(canvas)) return null;
    var dpr = window.devicePixelRatio || 1;
    var size = getLogicalSize(canvas);
    var pw = Math.round(size.w * dpr);
    var ph = Math.round(size.h * dpr);
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
  }

  function installCanvasObserver(canvas) {
    if (!canvas || canvasObservers.has(canvas) || typeof ResizeObserver === 'undefined') return;
    var ro = new ResizeObserver(function () {
      flushCanvas(canvas);
    });
    ro.observe(canvas);
    canvasObservers.set(canvas, ro);
  }

  function rememberCanvasState(canvas, state) {
    if (!canvas) return;
    var prev = canvasStates.get(canvas);
    if (prev && prev.kind === state.kind) {
      if (state.kind === 'bars') {
        prev.values = state.values;
        prev.progress = state.progress;
        prev.options = state.options;
      } else if (state.kind === 'scroll') {
        prev.samples = state.samples;
        prev.options = state.options;
      }
    } else {
      canvasStates.set(canvas, state);
    }
    scheduledCanvases.add(canvas);
    installCanvasObserver(canvas);
  }

  function forgetCanvasState(canvas) {
    if (!canvas) return;
    canvasStates.delete(canvas);
    scheduledCanvases.delete(canvas);
    var ro = canvasObservers.get(canvas);
    if (ro) {
      ro.disconnect();
      canvasObservers.delete(canvas);
    }
  }

  function flushCanvas(canvas) {
    var state = canvasStates.get(canvas);
    if (!state || !isMeasurable(canvas)) return;
    if (state.kind === 'bars') {
      paintBars(canvas, state.values, state.progress, state.options);
    } else if (state.kind === 'scroll') {
      paintScrollWaveform(canvas, state.samples, state.options);
    }
  }

  function redrawScheduled() {
    scheduledCanvases.forEach(function (canvas) {
      flushCanvas(canvas);
    });
  }

  function clearCanvas(canvas) {
    forgetCanvasState(canvas);
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  function barCountForWidth(width, barW, gap) {
    return Math.max(1, Math.floor((width + gap) / (barW + gap)));
  }

  function resampleValues(values, count) {
    if (!values || values.length === 0) return new Array(count).fill(0);
    if (values.length === count) return values;
    var out = new Array(count);
    for (var i = 0; i < count; i++) {
      var start = Math.floor(i * values.length / count);
      var end = Math.max(start + 1, Math.floor((i + 1) * values.length / count));
      var peak = 0;
      for (var j = start; j < end; j++) {
        var v = values[j] != null ? values[j] : 0;
        if (v > peak) peak = v;
      }
      out[i] = peak;
    }
    return out;
  }

  function getAccentColor(canvas) {
    var root = document.getElementById('product-builder-root');
    if (!root && canvas) root = canvas.closest('#product-builder-root');
    if (!root) return '#2E2E2E';
    var c = getComputedStyle(root).getPropertyValue('--pb-accent').trim();
    return c || '#2E2E2E';
  }

  function drawFineBar(ctx, x, midY, amp, maxHalfH, barW, color) {
    ctx.fillStyle = color;
    var height = amp < SILENCE_THRESHOLD ? barW : Math.max(barW, amp * maxHalfH * 2);
    ctx.beginPath();
    ctx.roundRect(x, midY - height / 2, barW, height, barW / 2);
    ctx.fill();
  }

  function paintScrollWaveform(canvas, samples, options) {
    var ctx = getCtx(canvas);
    if (!ctx) return;
    var opts = options || {};
    var barW = opts.barWidth || BAR_WIDTH;
    var gap = opts.barGap || BAR_GAP;
    var color = opts.color || '#111';
    var dotColor = opts.dotColor || '#c8c8c8';
    var size = getLogicalSize(canvas);
    var w = size.w;
    var h = size.h;
    var midY = h / 2;
    var maxHalfH = h * 0.4;
    var count = barCountForWidth(w, barW, gap);

    ctx.clearRect(0, 0, w, h);

    var vals = samples.slice(-count);
    var padLeft = count - vals.length;

    for (var i = 0; i < count; i++) {
      var x = i * (barW + gap);
      if (i < padLeft) {
        drawFineBar(ctx, x, midY, 0, maxHalfH, barW, dotColor);
      } else {
        drawFineBar(ctx, x, midY, vals[i - padLeft], maxHalfH, barW, color);
      }
    }
  }

  function drawScrollWaveform(canvas, samples, options) {
    if (!canvas) return;
    rememberCanvasState(canvas, {
      kind: 'scroll',
      samples: samples || [],
      options: options || {}
    });
    flushCanvas(canvas);
  }

  function paintBars(canvas, values, progress, options) {
    var ctx = getCtx(canvas);
    if (!ctx) return;
    var opts = options || {};
    var size = getLogicalSize(canvas);
    var w = size.w;
    var h = size.h;
    var midY = h / 2;
    var maxHalfH = h * 0.4;
    var barW = opts.barWidth || BAR_WIDTH;
    var gap = opts.barGap || BAR_GAP;
    var count = barCountForWidth(w, barW, gap);
    var vals = resampleValues(values, count);
    var playedColor = opts.playedColor || '#30302f';
    var unplayedColor = opts.unplayedColor || '#c8c8c8';
    var progressVal = typeof progress === 'number' && isFinite(progress)
      ? Math.max(0, Math.min(1, progress))
      : 0;

    ctx.clearRect(0, 0, w, h);

    for (var i = 0; i < count; i++) {
      var amp = vals[i] != null ? vals[i] : 0;
      var x = i * (barW + gap);
      var color = (i / count) < progressVal ? playedColor : unplayedColor;
      drawFineBar(ctx, x, midY, amp, maxHalfH, barW, color);
    }
  }

  function drawBars(canvas, values, progress, options) {
    if (!canvas) return;
    rememberCanvasState(canvas, {
      kind: 'bars',
      values: values || [],
      progress: progress,
      options: options || {}
    });
    flushCanvas(canvas);
  }

  function seekRatioFromPointer(canvas, clientX) {
    if (!canvas) return 0;
    var rect = canvas.getBoundingClientRect();
    if (!rect.width) return 0;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }

  function normalizePeaks(samples, barCount) {
    var out = new Array(barCount);
    var block = Math.max(1, Math.floor(samples.length / barCount));
    for (var i = 0; i < barCount; i++) {
      var start = i * block;
      var end = Math.min(samples.length, start + block);
      var peak = 0;
      for (var j = start; j < end; j++) {
        var v = Math.abs(samples[j] || 0);
        if (v > peak) peak = v;
      }
      out[i] = Math.min(1, peak * 2.5);
    }
    return out;
  }

  function createLiveAnalyser(stream) {
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || !stream) return null;
    var ctx = new AC();
    if (ctx.state === 'suspended') ctx.resume().catch(function () {});
    var source = ctx.createMediaStreamSource(stream);
    var analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    var data = new Uint8Array(analyser.frequencyBinCount);
    return {
      analyser: analyser,
      data: data,
      audioContext: ctx,
      stop: function () {
        try { source.disconnect(); } catch (e) {}
        try { ctx.close().catch(function () {}); } catch (e2) {}
      },
      readLevel: function () {
        analyser.getByteTimeDomainData(data);
        var sum = 0;
        for (var i = 0; i < data.length; i++) {
          var v = (data[i] - 128) / 128;
          sum += v * v;
        }
        return Math.min(1, Math.sqrt(sum / data.length) * 5);
      },
      sample: function () {
        analyser.getByteTimeDomainData(data);
        var peaks = new Array(BAR_COUNT);
        var step = Math.max(1, Math.floor(data.length / BAR_COUNT));
        for (var i = 0; i < BAR_COUNT; i++) {
          var max = 0;
          for (var j = 0; j < step; j++) {
            var idx = i * step + j;
            if (idx >= data.length) break;
            var v = Math.abs(data[idx] - 128) / 128;
            if (v > max) max = v;
          }
          peaks[i] = Math.min(1, max * 1.8);
        }
        return peaks;
      }
    };
  }

  function createLiveScroller(stream) {
    var analyser = createLiveAnalyser(stream);
    if (!analyser) return null;
    var history = [];
    var lastPush = 0;

    return {
      tick: function (now) {
        if (!now) now = performance.now();
        if (now - lastPush >= LIVE_SAMPLE_MS) {
          history.push(analyser.readLevel());
          lastPush = now;
        }
      },
      draw: function (canvas) {
        drawScrollWaveform(canvas, history);
      },
      getHistory: function () {
        return history.slice();
      },
      stop: function () {
        analyser.stop();
        history = [];
      }
    };
  }

  function decodePeaks(blob, barCount) {
    barCount = barCount || BAR_COUNT;
    var empty = { peaks: new Array(barCount).fill(0), duration: 0 };
    if (!blob) return Promise.resolve(empty);
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return Promise.resolve(empty);
    return blob.arrayBuffer()
      .then(function (buf) {
        var ctx = new AC();
        return ctx.decodeAudioData(buf).then(function (audioBuf) {
          var ch = audioBuf.getChannelData(0);
          var peaks = normalizePeaks(ch, barCount);
          var duration = audioBuf.duration && isFinite(audioBuf.duration) ? audioBuf.duration : 0;
          return { peaks: peaks, duration: duration };
        }).finally(function () { return ctx.close().catch(function () {}); });
      })
      .catch(function () {
        return empty;
      });
  }

  function formatTime(sec) {
    var s = Math.max(0, Math.floor(sec || 0));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return (m < 10 ? '0' + m : m) + ':' + (r < 10 ? '0' + r : r);
  }

  window.pbAudioWaveform = {
    BAR_COUNT: BAR_COUNT,
    drawBars: drawBars,
    drawScrollWaveform: drawScrollWaveform,
    clearCanvas: clearCanvas,
    redrawScheduled: redrawScheduled,
    getAccentColor: getAccentColor,
    createLiveAnalyser: createLiveAnalyser,
    createLiveScroller: createLiveScroller,
    decodePeaks: decodePeaks,
    seekRatioFromPointer: seekRatioFromPointer,
    formatTime: formatTime,
    resampleValues: resampleValues
  };
})();
